import { identity } from 'ramda'
import { isFunction, isSortActive, propOrPath } from '@btime/helpers'
import { useChangeSort, useSort } from '@btime/react-contexts'
import React from 'react'

import { ExpandedTableRow, Table, TableCol, TableContent, TableRow } from './Table'
import { Grid } from '../structure/Grid'
import { useMultiSelectColumns } from '../multiSelect/multiSelectColumn'
import ConditionalLink from '../actions/ConditionalLink'
import SortIndicator from '../helpers/SortIndicator'
import Text from '../presentation/Text'

const DataTableHeaderCol = ({ label, renderHeader, headerSideContent, dataIndex, sortIndex, onClick, ...props }) => {
  const sortField = sortIndex || dataIndex
  const sort = useSort()
  const setSort = useChangeSort()

  const canSort = sortIndex !== false && dataIndex && setSort
  const isSortingField = isSortActive(sortField, sort || {})
  let handleClick = onClick
  if (canSort) handleClick = () => setSort(sortField)

  return (
    <TableCol {...props}>
      <Grid gap={0.2} align="center">
        <ConditionalLink onClick={handleClick}>
          {renderHeader ? (
            renderHeader(label)
          ) : (
            <Text bold color="dark2" size="xsmall">
              {label}
            </Text>
          )}
        </ConditionalLink>
        <SortIndicator active={isSortingField} type={sort?.type} />
        {headerSideContent}
      </Grid>
    </TableCol>
  )
}

const DataTableCol = ({ format, render, dataIndex, record, onClick, index, ...props }) => {
  format = format || identity

  const value = dataIndex ? propOrPath('', dataIndex, record) : record
  const formattedValue = format(value, record, index)

  return (
    <TableCol {...props}>
      <ConditionalLink onClick={onClick ? () => onClick(value, record) : undefined}>
        {render ? render(formattedValue, record, index, props) : <Text {...props}>{formattedValue}</Text>}
      </ConditionalLink>
    </TableCol>
  )
}

const getInitialExpandIndex = (columns, record) => {
  const initialIndex = columns.findIndex(col => col.initialExpandValue)
  if (initialIndex < 0) {
    return undefined
  }
  const initialValue = columns[initialIndex]
  let result = initialValue?.initialExpandValue
  if (isFunction(result)) {
    result = result(record)
  }
  if (result === true) {
    return initialIndex
  }
  return undefined
}

const DataTableRow = ({ inPanel, size, columns, record, index, showDeleted }) => {
  const initialExpandIndex = getInitialExpandIndex(columns, record)
  const [expandedColIndex, setExpandedColIndex] = React.useState(initialExpandIndex)
  const expandedCol = expandedColIndex >= 0 && columns[expandedColIndex]
  const showDeletedStyled = showDeleted && isFunction(showDeleted) && showDeleted(record)
  return (
    <>
      <TableRow key={`row-${record.id}`} hasSidePadding={inPanel} size={size} showDeleted={showDeletedStyled}>
        {columns.map(({ label, ...column }, colIndex) => {
          const isExpanded = expandedColIndex === colIndex
          const { onExpandOrCollapse } = column
          const toggleExpand = () => {
            const newIndex = isExpanded ? undefined : colIndex
            onExpandOrCollapse && onExpandOrCollapse(record, newIndex >= 0, newIndex)
            setExpandedColIndex(newIndex)
          }

          return (
            <DataTableCol
              key={`col-${record.id}-${label}`}
              {...column}
              record={record}
              index={index}
              toggleExpand={toggleExpand}
              isExpanded={isExpanded}
            />
          )
        })}
      </TableRow>

      {expandedCol && <ExpandedTableRow smallPadding={!inPanel}>{expandedCol.renderExpand(record)}</ExpandedTableRow>}
    </>
  )
}

const DataTable = ({ data, onSelectAll, columns, inPanel, size, hideHeader, showDeleted }) => {
  columns = [...useMultiSelectColumns(data, onSelectAll), ...columns]
  if (!data?.length) return false

  return (
    <Table>
      {!hideHeader && (
        <TableRow head hasSidePadding={inPanel} size={size}>
          {columns.map(column => (
            <DataTableHeaderCol key={`head-${column.label}`} {...column} />
          ))}
        </TableRow>
      )}
      <TableContent inPanel={inPanel}>
        {data.map((record, index) => (
          <DataTableRow
            showDeleted={showDeleted}
            key={`row-${record.id}`}
            record={record}
            index={index}
            inPanel={inPanel}
            size={size}
            columns={columns}
          />
        ))}
      </TableContent>
    </Table>
  )
}

export default DataTable
